import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import suiteService from "../../services/SuiteService";
import { Suite } from "../../interfaces";

interface SuiteState {
  suite: Suite[];
  suiteCases: any[];
  total: number;
  error?: string | null;
  loading: boolean;
  singleSuite?: Suite;
}

const initialState: SuiteState = {
  suite: [],
  suiteCases: [],
  total: 0,
  singleSuite: {} as Suite,
  error: null,
  loading: false,
};

export const fetchSuites = createAsyncThunk(
  "suite/fetchSuites",
  async (params: string, { rejectWithValue }) => {
    try {
      const response = await suiteService.getAll(params);
      return response.data;
    } catch (error) {
      return error;
    }
  }
);

export const searchSuiteCases = createAsyncThunk(
  "suite/searchSuites",
  async (data: any) => {
    try {
      const response = await suiteService.search(data);
      return response.data;
    } catch (error) {
      return error;
    }
  }
);

export const addSuite = createAsyncThunk(
  "suite/addSuite",
  async (data: any) => {
    try {
      const response = await suiteService.post(data);
      return response.data;
    } catch (error) {
      return error;
    }
  }
);

export const updateSuite = createAsyncThunk(
  "suite/updateSuite",
  async (data: any) => {
    try {
      const response = await suiteService.put(data.id, {
        title: data.title,
        meta_data: data.meta_data,
        status: data.status,
        planed_start: data.planed_start,
      });
      return response.data;
    } catch (error) {
      return error;
    }
  }
);

export const deleteSuite = createAsyncThunk(
  "runs/removeRun",
  async (id: number | string) => {
    try {
      const response = await suiteService.remove(id);
      return response.data;
    } catch (error) {
      return error;
    }
  }
);
export const getSuiteById = createAsyncThunk(
  "suite/getSuiteById",
  async (id: number | string) => {
    try {
      const response = await suiteService.getById(id);
      return response.data;
    } catch (error) {
      return error;
    }
  }
);

export const exportSuite = createAsyncThunk(
  "suite/exportSuite",
  async (params: string) => {
    try {
      const response = await suiteService.exportSuite(params);
      return response.data;
    } catch (error) {
      return error;
    }
  }
);
export const exportAutocomplete = createAsyncThunk(
  "suite/exportAutocomplete",
  async (params: string) => {
    try {
      const response = await suiteService.exportAutocomplete(params);
      return response.data;
    } catch (error) {
      return error;
    }
  }
);
export const cloneSuite = createAsyncThunk(
  "suite/cloneSuite",
  async (id: number | string) => {
    try {
      const response = await suiteService.cloneSuite(id);
      return response.data;
    } catch (error) {
      return error;
    }
  }
);
export const exportSuiteConfig = createAsyncThunk(
  "suite/exportConfig",
  async (suiteId: number | string, { rejectWithValue }) => {
    try {
      const response = await suiteService.exportSuiteConfig(suiteId);
      return response.data;
    } catch (error) {
      return error;
    }
  }
);
export const abortSuite = createAsyncThunk(
  "suite/abortSuite",
  async (id, { rejectWithValue }) => {
    try {
      const response = await suiteService.abortSuite(id);
      return { id, ...response.data };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const rerunSuite = createAsyncThunk(
  "suite/rerunSuite",
  async (id, { rejectWithValue }) => {
    try {
      const response = await suiteService.rerunSuite(id);
      return { id, ...response.data };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
const suitSlice = createSlice({
  name: "suite",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchSuites.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchSuites.fulfilled, (state, action) => {
      state.suite = action.payload.suites;
      state.total = action.payload.total;
      state.loading = false;
    });
    builder.addCase(fetchSuites.rejected, (state, action) => {
      state.error = action.error.message;
      state.loading = false;
    });

    builder.addCase(addSuite.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(addSuite.fulfilled, (state, action) => {
      state.suite.push(action.payload);
      state.loading = false;
    });
    builder.addCase(addSuite.rejected, (state, action) => {
      state.error = action.error.message;
      state.loading = false;
    });

    builder.addCase(updateSuite.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateSuite.fulfilled, (state, action) => {
      state.suite = state.suite.map((suit) =>
        suit.id === action.payload.id ? action.payload : suit
      );
      state.loading = false;
    });
    builder.addCase(updateSuite.rejected, (state, action) => {
      state.error = action.error.message;
      state.loading = false;
    });

    builder.addCase(deleteSuite.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteSuite.fulfilled, (state, action) => {
      state.suite = state.suite.filter((suit) => suit.id !== action.payload.id);
      state.loading = false;
    });
    builder.addCase(deleteSuite.rejected, (state, action) => {
      state.error = action.error.message;
      state.loading = false;
    });
    builder.addCase(getSuiteById.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getSuiteById.fulfilled, (state, action) => {
      state.singleSuite = action.payload;
      state.loading = false;
    });
    builder.addCase(getSuiteById.rejected, (state, action) => {
      state.error = action.error.message;
      state.loading = false;
    });
  },
});

const { reducer } = suitSlice;

export default reducer;
